/**
 * Supplies program — `/api/supplies/*`. The orderable catalog (tiles grouped
 * by line with "Ships in 3-5 days") and a school's bulk orders, billed to the
 * school account at submit. `idempotencyKey` is minted per dialog open, like
 * the POS register, so a retried submit returns the same order.
 */
import type { SupplyLineCode } from '@ordo/constants/supply-taxonomy'
import type { SupplyDeliveryPromise } from '@ordo/schema/src/managers/supply-delivery'
import type { SupplyCasePolicy } from '@ordo/schema/src/managers/supply-minimum'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './http'

export interface SupplyCatalogItemWire {
  casePolicies?: SupplyCasePolicy[]
  isCase?: boolean
  allergens?: string[]
  delivery?: SupplyDeliveryPromise | null
  supplyItemId: string
  name: string
  lineCode: SupplyLineCode
  description: string | null
  unitLabel: string
  packQuantity: number | null
  customerPriceCents: number
  currency: string
  shipMinDays: number
  shipMaxDays: number
  shipLabel: string
  imageAssetId: string | null
  imageUrl?: string | null
}

export interface SupplyCatalogLineWire {
  lineCode: SupplyLineCode
  label: string
  description: string
  items: SupplyCatalogItemWire[]
}

export type SupplyOrderStatus = 'DRAFT' | 'SUBMITTED' | 'SHIPPED' | 'DELIVERED' | 'CANCELLED'

export interface SupplyOrderLineWire {
  id: string
  supplyItemId: string
  lineCode: SupplyLineCode
  name: string
  unitLabel: string
  quantity: number
  unitPriceCents: number
  totalCents: number
  shipMinDays: number
  shipMaxDays: number
}

export interface SupplyOrderWire {
  id: string
  schoolLocationId: string
  status: SupplyOrderStatus
  statusLabel: string
  submittedAt: string | null
  shippedAt: string | null
  deliveredAt: string | null
  cancelledAt: string | null
  shipLabel: string
  subtotalCents: number
  taxCents: number
  totalCents: number
  currency: string
  carrier: string | null
  trackingNumber: string | null
  notes: string | null
  lines: SupplyOrderLineWire[]
  createdAt: string
}

export interface SubmitSupplyOrderInput {
  idempotencyKey: string
  locationId: string
  lines: Array<{ supplyItemId: string; quantity: number; expectedUnitPriceCents: number }>
  notes?: string | null
}

export function supplyCatalogQueryKey(locationId: string) {
  return ['supplies', 'catalog', locationId] as const
}

export function supplyOrdersQueryKey(locationId: string) {
  return ['supplies', 'orders', locationId] as const
}

export function useSupplyCatalogQuery(locationId: string, opts?: { enabled?: boolean }) {
  return useQuery<{ lines: SupplyCatalogLineWire[] }>({
    queryKey: supplyCatalogQueryKey(locationId),
    queryFn: ({ signal }) => apiFetch('/api/supplies/catalog', { query: { locationId }, signal }),
    enabled: opts?.enabled ?? Boolean(locationId),
  })
}

export function useSupplyOrdersQuery(locationId: string, opts?: { enabled?: boolean }) {
  return useQuery<{ orders: SupplyOrderWire[] }>({
    queryKey: supplyOrdersQueryKey(locationId),
    queryFn: ({ signal }) => apiFetch('/api/supplies/orders', { query: { locationId }, signal }),
    enabled: opts?.enabled ?? Boolean(locationId),
  })
}

export function useSubmitSupplyOrderMutation(locationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: SubmitSupplyOrderInput) =>
      apiFetch<{ order: SupplyOrderWire; replayed: boolean }>('/api/supplies/orders', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: supplyOrdersQueryKey(locationId) })
    },
  })
}
